import type {
  ConditionKind,
  NodePath,
  TreeConditionNode,
  TreeGroupNode,
} from '@/components/tree-dnd/tree-dnd-types';
import { isConditionNode } from '@/components/tree-dnd/tree-dnd-types';
import { VISIBLE_CONDITION_KINDS } from '@/components/tree-dnd/tree-dnd-conditions/registry';
import { ConditionRenderer } from '@/components/tree-dnd/tree-dnd-conditions/condition-renderer';
import { useTreeDndStore, useTreeDndUiStore } from '@/components/tree-dnd';
import { useConditionLiveSummary } from '@/components/audiences/use-condition-live-summary';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { useI18nContext } from '@/i18n/i18n-react';
import { cn } from '@/lib/utils';

type RootConditionEntry = {
  path: NodePath;
  node: TreeConditionNode;
};

function indexRootConditionsByKind(
  tree: TreeGroupNode
): Partial<Record<ConditionKind, RootConditionEntry>> {
  const byKind: Partial<Record<ConditionKind, RootConditionEntry>> = {};

  tree.children.forEach((child, index) => {
    if (!isConditionNode(child)) return;
    if (byKind[child.kind]) return;
    byKind[child.kind] = { path: [index], node: child };
  });

  return byKind;
}

function useConditionLabels() {
  const { LL } = useI18nContext();

  return {
    titles: {
      installedApp: LL.audiences.filters.conditions.installedApp,
      boughtProduct: LL.audiences.filters.conditions.boughtProduct,
      receivedNotification:
        LL.audiences.filters.conditions.receivedNotification,
      clickedCampaign: LL.audiences.filters.conditions.clickedCampaign,
      rfmGroup: LL.audiences.filters.conditions.rfmGroup,
      lastNotificationAt: LL.audiences.filters.conditions.lastNotificationAt,
      lastAppAccessAt: LL.audiences.filters.conditions.lastAppAccessAt,
      viewedProduct: LL.audiences.filters.conditions.viewedProduct,
      location: LL.audiences.filters.conditions.location,
      lastOrder: LL.audiences.filters.conditions.lastOrder,
      orderFrequency: LL.audiences.filters.conditions.orderFrequency,
      totalSpend: LL.audiences.filters.conditions.totalSpend,
      hasNotificationsEnabled:
        LL.audiences.filters.conditions.hasNotificationsEnabled,
    },
    descriptions: {
      installedApp: LL.audiences.filters.addSheet.descriptions.installedApp,
      boughtProduct: LL.audiences.filters.addSheet.descriptions.boughtProduct,
      receivedNotification:
        LL.audiences.filters.addSheet.descriptions.receivedNotification,
      clickedCampaign:
        LL.audiences.filters.addSheet.descriptions.clickedCampaign,
      rfmGroup: LL.audiences.filters.addSheet.descriptions.rfmGroup,
      lastNotificationAt:
        LL.audiences.filters.addSheet.descriptions.lastNotificationAt,
      lastAppAccessAt:
        LL.audiences.filters.addSheet.descriptions.lastAppAccessAt,
      viewedProduct: LL.audiences.filters.addSheet.descriptions.viewedProduct,
      location: LL.audiences.filters.addSheet.descriptions.location,
      lastOrder: LL.audiences.filters.addSheet.descriptions.lastOrder,
      orderFrequency: LL.audiences.filters.addSheet.descriptions.orderFrequency,
      totalSpend: LL.audiences.filters.addSheet.descriptions.totalSpend,
      hasNotificationsEnabled:
        LL.audiences.filters.addSheet.descriptions.hasNotificationsEnabled,
    },
  } as const;
}

function EnabledFilterDescription({ node }: { node: TreeConditionNode }) {
  const summary = useConditionLiveSummary(node);
  return <>{summary}</>;
}

interface SimpleFilterCardProps {
  kind: ConditionKind;
  entry: RootConditionEntry | undefined;
  disabled: boolean;
  entryDelayMultiplier?: number;
  onToggle: (enabled: boolean) => void;
  onEdit: () => void;
}

function SimpleFilterCard({
  kind,
  entry,
  disabled,
  entryDelayMultiplier = 1,
  onToggle,
  onEdit,
}: SimpleFilterCardProps) {
  const { LL } = useI18nContext();
  const labels = useConditionLabels();
  const enabled = Boolean(entry);
  const title = labels.titles[kind]();
  const catalogDescription = labels.descriptions[kind]();

  return (
    <Card
      className={cn(
        'entry-fade-in-up transition-opacity',
        !enabled && 'opacity-80',
        disabled && 'pointer-events-none opacity-60'
      )}
      style={{
        animationDelay: `calc(var(--entry-delay-base)/4 * ${entryDelayMultiplier})`,
      }}
    >
      <CardHeader className="flex flex-row items-start gap-3 space-y-0 p-4">
        <Switch
          size="compact"
          checked={enabled}
          disabled={disabled}
          onCheckedChange={onToggle}
          aria-label={title}
          className="mt-0.5 shrink-0"
        />
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <CardTitle className="text-sm font-medium leading-snug">
              {title}
            </CardTitle>
            {enabled ? (
              <Button
                type="button"
                variant="ghost-primary"
                size="compact"
                className="shrink-0"
                disabled={disabled}
                aria-label={LL.audiences.filters.editConditionAriaLabel({
                  condition: title,
                })}
                onClick={onEdit}
              >
                {LL.audiences.details.overview.filterConditions.edit()}
              </Button>
            ) : null}
          </div>
          <CardDescription className="text-sm">
            {entry ? (
              <EnabledFilterDescription node={entry.node} />
            ) : (
              catalogDescription
            )}
          </CardDescription>
        </div>
      </CardHeader>
    </Card>
  );
}

export function AudienceSimpleFilterBuilder({
  className,
}: {
  className?: string;
}) {
  const tree = useTreeDndStore((state) => state.tree);
  const addCondition = useTreeDndStore((state) => state.addCondition);
  const removeNode = useTreeDndStore((state) => state.removeNode);
  const isApplyingAiChanges = useTreeDndStore(
    (state) => state.isApplyingAiChanges
  );
  const requestConditionEditor = useTreeDndUiStore(
    (state) => state.requestConditionEditor
  );

  const byKind = indexRootConditionsByKind(tree);
  const enabledEntries = VISIBLE_CONDITION_KINDS.flatMap((kind) => {
    const entry = byKind[kind];
    return entry ? [entry] : [];
  });

  const handleToggle = (kind: ConditionKind, enabled: boolean) => {
    if (isApplyingAiChanges) return;

    if (enabled) {
      const existing = byKind[kind];
      if (existing) {
        requestConditionEditor(existing.node.id);
        return;
      }
      const nodeId = addCondition([], kind);
      if (nodeId) {
        requestConditionEditor(nodeId);
      }
      return;
    }

    const existing = byKind[kind];
    if (existing) {
      removeNode(existing.path);
    }
  };

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {VISIBLE_CONDITION_KINDS.map((kind, index) => (
        <SimpleFilterCard
          key={kind}
          kind={kind}
          entry={byKind[kind]}
          disabled={isApplyingAiChanges}
          entryDelayMultiplier={index + 1}
          onToggle={(enabled) => handleToggle(kind, enabled)}
          onEdit={() => {
            const entry = byKind[kind];
            if (entry) {
              requestConditionEditor(entry.node.id);
            }
          }}
        />
      ))}

      {/* Mount condition editors so existing sheets open via requestConditionEditor */}
      <div className="hidden" aria-hidden="true">
        {enabledEntries.map((entry) => (
          <ConditionRenderer
            key={entry.node.id}
            path={entry.path}
            node={entry.node}
            showAddAnother={false}
            showMoreOptions={false}
          />
        ))}
      </div>
    </div>
  );
}
